// React Query hooks for the EmployeeConnect timesheet (/api/timesheet).
// Kept apart from useQueries.js, which is already very long; the request path
// is the same shared apiRequest.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import useAuthenticatedFetch from './useAuthenticatedFetch';
import { apiRequest } from './useQueries';

const qs = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    search.set(key, Array.isArray(value) ? value.join(',') : String(value));
  });
  const s = search.toString();
  return s ? `?${s}` : '';
};

const useApi = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  return (endpoint, options) => apiRequest(authenticatedFetch, `/timesheet${endpoint}`, options);
};

// Everything that reads entries or periods, so one write can refresh them all.
// Returned so a mutation resolves only once what is on screen has caught up.
// The validation checks rerun too, but in the background: a save should not
// wait on them.
const invalidateTimesheet = (queryClient) => {
  queryClient.invalidateQueries({ queryKey: ['tsValidation'] });
  queryClient.invalidateQueries({ queryKey: ['tsHistory'] });
  return Promise.all(
    ['tsEntries', 'tsPeriod', 'tsApprovals', 'tsReports', 'tsRecentTasks', 'tsTasks']
      .map((key) => queryClient.invalidateQueries({ queryKey: [key] }))
  );
};

export const useTimesheetConfig = (options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsConfig'],
    queryFn: () => api('/config'),
    staleTime: 60 * 1000,
    retry: 1,
    ...options,
  });
};

export const useSaveTimesheetSettings = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api('/settings', { method: 'PUT', body }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tsConfig'] }),
  });
};

export const useTimesheetTasks = ({ includeArchived = false, usage = false } = {}, options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsTasks', includeArchived, usage],
    queryFn: () => api(`/tasks${qs({ includeArchived: includeArchived ? 1 : '', usage: usage ? 1 : '' })}`),
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useRecentTimesheetTasks = (options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsRecentTasks'],
    queryFn: () => api('/tasks/recent'),
    staleTime: 60 * 1000,
    ...options,
  });
};

export const useSaveTimesheetTask = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => (id
      ? api(`/tasks/${id}`, { method: 'PUT', body })
      : api('/tasks', { method: 'POST', body })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tsTasks'] }),
  });
};

// The top-level projects' display order, as ids top to bottom. Every cached
// task list is regrouped first so a dropped project stays where it was dropped.
export const useReorderTimesheetProjects = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => api('/tasks/order', { method: 'PUT', body: { ids } }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ['tsTasks'] });
      const previous = queryClient.getQueriesData({ queryKey: ['tsTasks'] });
      const rank = new Map(ids.map((id, idx) => [id, idx]));
      const at = (t) => (rank.has(t.rootId) ? rank.get(t.rootId) : ids.length);
      queryClient.setQueriesData({ queryKey: ['tsTasks'] }, (old) => (old?.tasks
        ? {
          ...old,
          // A stable sort, so each project keeps its own tree order inside.
          tasks: [...old.tasks]
            .sort((a, b) => at(a) - at(b))
            .map((t) => (rank.has(t.id) ? { ...t, sortOrder: rank.get(t.id) } : t)),
        }
        : old));
      return { previous };
    },
    onError: (err, ids, context) => {
      (context?.previous || []).forEach(([key, data]) => queryClient.setQueryData(key, data));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['tsTasks'] }),
  });
};

// ProPlanConnect's projects and task orders, for the link pickers in
// Timesheet Admin: { configured, projects: [{ id, name, projectNumber, client,
// status, linkedTask, taskOrders: [{ id, name, number, linkedTask }] }] }.
export const useProPlanProjects = (options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsProPlanProjects'],
    queryFn: () => api('/proplan/projects'),
    staleTime: 5 * 60 * 1000,
    retry: false,
    ...options,
  });
};

// { id, proplanProjectId } for a project or { id, proplanTaskOrderId } for a
// task; null unlinks.
export const useSaveProPlanLink = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => api(`/tasks/${id}/proplan`, { method: 'PUT', body }),
    onSuccess: () => Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tsTasks'] }),
      queryClient.invalidateQueries({ queryKey: ['tsProPlanProjects'] }),
    ]),
  });
};

export const useDeleteTimesheetTask = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api(`/tasks/${id}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['tsTasks'] }),
  });
};

export const useTimesheetEntries = ({ employeeId, start, end } = {}, options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsEntries', employeeId || 'me', start, end],
    queryFn: () => api(`/entries${qs({ employeeId, start, end })}`),
    enabled: Boolean(start && end) && (options.enabled ?? true),
    staleTime: 15 * 1000,
    ...options,
  });
};

// The signed-in person's Outlook events for a range: { events, fetchedAt, stale }. Asked for by
// the week, kept for as long as the server keeps it, and not re-read on focus: only the refresh
// button (below) goes back early.
const CALENDAR_STALE_MS = 15 * 60 * 1000;

export const useTimesheetCalendar = ({ start, end }, options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsCalendar', start, end],
    queryFn: () => api(`/calendar${qs({ start, end })}`),
    staleTime: CALENDAR_STALE_MS,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    ...options,
  });
};

export const useRefreshTimesheetCalendar = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ start, end }) => api(`/calendar${qs({ start, end, refresh: 1 })}`),
    onSuccess: (data, { start, end }) => queryClient.setQueryData(['tsCalendar', start, end], data),
  });
};

export const useSaveTimesheetEntry = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['tsSaveEntry'],
    mutationFn: ({ id, ...body }) => (id
      ? api(`/entries/${id}`, { method: 'PUT', body })
      : api('/entries', { method: 'POST', body })),
    onSuccess: () => invalidateTimesheet(queryClient),
  });
};

export const useDeleteTimesheetEntry = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ['tsDeleteEntry'],
    mutationFn: (id) => api(`/entries/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateTimesheet(queryClient),
  });
};

// The View Hours rules over one employee's timesheet for a range (normally a
// pay period): { period, validations }. See GET /api/timesheet/validation.
export const useTimesheetValidation = ({ employeeId, start, end }, options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsValidation', employeeId || 'me', start, end],
    queryFn: () => api(`/validation${qs({ employeeId, start, end })}`),
    staleTime: 30 * 1000,
    ...options,
  });
};

// The configurable rules (GET /api/timesheet/rules): { installed, rules,
// catalog }. Each rule carries a plain-words `summary`; the catalog is every
// choice the editor offers, owned by the server.
export const useTimesheetRules = (options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsRules'],
    queryFn: () => api('/rules'),
    staleTime: 60 * 1000,
    ...options,
  });
};

// A rule change changes what the checks say, so both refresh.
const invalidateRules = (queryClient) => Promise.all([
  queryClient.invalidateQueries({ queryKey: ['tsRules'] }),
  queryClient.invalidateQueries({ queryKey: ['tsValidation'] }),
]);

export const useSaveTimesheetRule = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...body }) => (id
      ? api(`/rules/${id}`, { method: 'PUT', body })
      : api('/rules', { method: 'POST', body })),
    onSuccess: () => invalidateRules(queryClient),
  });
};

export const useDeleteTimesheetRule = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => api(`/rules/${id}`, { method: 'DELETE' }),
    onSuccess: () => invalidateRules(queryClient),
  });
};

// The whole order, top to bottom. The list is reordered in the cache first so
// a dropped card stays where it was dropped.
export const useReorderTimesheetRules = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => api('/rules/order', { method: 'PUT', body: { ids } }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: ['tsRules'] });
      const previous = queryClient.getQueryData(['tsRules']);
      if (previous?.rules) {
        const byId = new Map(previous.rules.map((r) => [r.id, r]));
        queryClient.setQueryData(['tsRules'], { ...previous, rules: ids.map((id) => byId.get(id)).filter(Boolean) });
      }
      return { previous };
    },
    onError: (err, ids, context) => {
      if (context?.previous) queryClient.setQueryData(['tsRules'], context.previous);
    },
    onSettled: () => invalidateRules(queryClient),
  });
};

// What changed on entries (timesheet_entry_history): one entry's history, or
// every change to someone's entries in a range. { installed, events }.
export const useEntryHistory = (entryId, options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsHistory', 'entry', entryId],
    queryFn: () => api(`/entries/${entryId}/history`),
    staleTime: 30 * 1000,
    ...options,
  });
};

export const useTimesheetHistory = ({ employeeId, start, end }, options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsHistory', 'range', employeeId || 'me', start, end],
    queryFn: () => api(`/history${qs({ employeeId, start, end })}`),
    staleTime: 30 * 1000,
    ...options,
  });
};

// The caller's pinned tasks for the task picker: { taskIds, max }.
export const useTimesheetFavorites = (options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsFavorites'],
    queryFn: () => api('/favorites'),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

// Saves the whole list. The cache is updated first so a star toggles at once,
// and put back if the save fails.
export const useSaveTimesheetFavorites = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (taskIds) => api('/favorites', { method: 'PUT', body: { taskIds } }),
    onMutate: async (taskIds) => {
      await queryClient.cancelQueries({ queryKey: ['tsFavorites'] });
      const previous = queryClient.getQueryData(['tsFavorites']);
      queryClient.setQueryData(['tsFavorites'], (old) => ({ ...(old || {}), taskIds }));
      return { previous };
    },
    onError: (err, taskIds, context) => {
      if (context?.previous) queryClient.setQueryData(['tsFavorites'], context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['tsFavorites'] }),
  });
};

export const useTimesheetTimer = (options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsTimer'],
    queryFn: () => api('/timer'),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    ...options,
  });
};

const useTimerMutation = (fn) => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => fn(api, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tsTimer'] });
      invalidateTimesheet(queryClient);
    },
  });
};

export const useStartTimer = () => useTimerMutation((api, body) => api('/timer/start', { method: 'POST', body }));
export const useUpdateTimer = () => useTimerMutation((api, body) => api('/timer', { method: 'PUT', body }));
export const useStopTimer = () => useTimerMutation((api, body) => api('/timer/stop', { method: 'POST', body }));
export const useDiscardTimer = () => useTimerMutation((api) => api('/timer', { method: 'DELETE' }));

export const useTimesheetPeriod = ({ employeeId, start } = {}, options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsPeriod', employeeId || 'me', start],
    queryFn: () => api(`/period${qs({ employeeId, start })}`),
    enabled: Boolean(start) && (options.enabled ?? true),
    staleTime: 15 * 1000,
    ...options,
  });
};

// action: submit | approve | return | reopen
export const usePeriodAction = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ action, ...body }) => api(`/period/${action}`, { method: 'POST', body }),
    onSuccess: () => invalidateTimesheet(queryClient),
  });
};

export const useBulkApprovePeriods = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => api('/period/approve-bulk', { method: 'POST', body }),
    onSuccess: () => invalidateTimesheet(queryClient),
  });
};

export const useTimesheetApprovals = ({ start } = {}, options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsApprovals', start],
    queryFn: () => api(`/approvals${qs({ start })}`),
    enabled: Boolean(start) && (options.enabled ?? true),
    staleTime: 15 * 1000,
    ...options,
  });
};

export const useTimesheetReport = (params = {}, options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsReports', params],
    queryFn: () => api(`/reports${qs(params)}`),
    enabled: Boolean(params.start && params.end) && (options.enabled ?? true),
    staleTime: 30 * 1000,
    ...options,
  });
};

// One-off pull of a report for an export, outside the cache: the entries
// themselves unless `includeEntries` is false (a summary needs only groups).
export const useTimesheetReportExport = () => {
  const api = useApi();
  return useMutation({
    mutationFn: ({ includeEntries = true, ...params }) => api(`/reports${qs({ ...params, includeEntries: includeEntries ? 1 : '' })}`),
  });
};

export const useTimesheetEmployees = (options = {}) => {
  const api = useApi();
  return useQuery({
    queryKey: ['tsEmployees'],
    queryFn: () => api('/employees'),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useTimesheetAdminAction = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    // kind: time-off-sync | import/catalog | import/entries
    mutationFn: ({ kind, ...body }) => api(`/admin/${kind}`, { method: 'POST', body }),
    onSuccess: () => invalidateTimesheet(queryClient),
  });
};

// Copy the entries of the most recent earlier day that has any (within two
// weeks) onto `date`: same tasks, notes and times. Time off is left behind -
// it follows its request, not the calendar.
export const useCopyPreviousDay = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, date, fromStart }) => {
      const { entries = [] } = await api(`/entries${qs({ employeeId, start: fromStart, end: date })}`);
      const candidates = entries.filter((e) => e.date < date && e.source !== 'time_off');
      const from = candidates.map((e) => e.date).sort().pop();
      if (!from) return { from: null, copied: 0, failed: 0 };
      let copied = 0;
      let failed = 0;
      for (const e of candidates.filter((x) => x.date === from)) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await api('/entries', {
            method: 'POST',
            body: {
              employeeId,
              taskId: e.taskId,
              date,
              startTime: e.startTime,
              endTime: e.endTime,
              durationSeconds: e.durationSeconds,
              description: e.description,
            },
          });
          copied += 1;
        } catch {
          failed += 1;
        }
      }
      return { from, copied, failed };
    },
    onSuccess: () => invalidateTimesheet(queryClient),
  });
};
